import { useCallback, useMemo } from 'react'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import {
  GetEvidenceIndexPageRequest,
  EvidenceWrapperResponse,
  GetEvidenceGroupByIdRequest,
  GetEvidenceGroupByIdResponse,
  GetLatestEvidenceGroupsRequest,
  GetLatestEvidenceGroupsResponse,
  GetLatestEvidenceRequest,
  ListEvidenceGroupsRequest,
  ListEvidenceGroupsResponse,
  ListEvidenceRequest,
  GetEvidenceInstanceListRequest,
  GetControlEvidenceTabRequest,
  GetRequestEvidenceTabRequest,
  LinkControlsToEvidenceRequest,
  LinkRequestsToEvidenceRequest,
  GetEvidenceControlsRequest,
  GetEvidenceControlsResponse,
  EVIDENCE_READ_ONLY_FIELDS,
  UpdateEvidenceReadOnlyFieldsRequest,
  EvidenceReadOnlyFields,
  EvidenceReadOnlyField,
  EvidenceWrapper,
  GetEvidenceGroupIfExistsResponse,
  GetEvidenceGroupIfExistsRequest,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  GetRelevantDateFilterRequest,
  GetRelevantDateFilterResponse,
  UpdateCaptionRequest,
} from '@trustero/trustero-api-web/lib/evidence/service_pb'
import { EvidenceServicePromiseClient } from '@trustero/trustero-api-web/lib/evidence/service_grpc_web_pb'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { useLocation, useParams } from 'react-router-dom'
import { useImportedRecordModal } from 'src/components/ModalForms/useImportedRecordModal'
import {
  GRC_PLATFORM,
  ReadOnlyFieldDetail,
} from '@trustero/trustero-api-web/lib/common/grc_platform_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import queryString, { ParsedQuery } from 'query-string'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  ListDirectoryScanSettingsRequest,
  ListDirectoryScanSettingsResponse,
} from '@trustero/trustero-api-web/lib/receptorutil/receptorutil_pb'
import { ReceptorUtilPromiseClient } from '@trustero/trustero-api-web/lib/receptorutil/receptorutil_grpc_web_pb'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import {
  AuditRecords,
  EvidenceFilter,
  GetAuditsRequest,
} from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useGrpcRevalidateByMethod } from 'src/components'
import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import { EvidenceManagementPromiseClient } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_grpc_web_pb'
import { EvidenceFilter as EvidenceManagementEvidenceFilter } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { ReceptorPromiseClient } from '@trustero/trustero-api-web/lib/agent/receptor_grpc_web_pb'
import { useInvalidateReceptorsCache } from 'src/components/async/model/receptor/useReceptors'
import { useFetchDocumentBodyAllowMultipart } from 'src/components/async/document/useDocument'
import { isParsedPartArray } from 'src/Utils/globalHelpers'
import { isEvidenceFile } from 'src/components/ModalForms/Evidence/evidenceUtils'
import { documentBodyAsFile } from 'src/adapter/AttachmentAdapter'
import { useInAudit } from 'src/context/AuditContext'
import cloneDeep from 'lodash/cloneDeep'
import { useNewModel } from 'src/components/async/hooks/useNewModel'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { getEvidenceOriginalFile } from 'src/Utils/Evidence/evidence.helpers'
import { formatEvidenceCaption } from 'src/Utils/helpers/string.helpers'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { formatTimestamp } from 'src/Utils/formatDate'
import {
  ControlFilter,
  GetControlToEvidenceMappingsRequest,
  GetControlToEvidenceMappingsResponse,
  GetEvidenceToControlMappingsRequest,
  GetEvidenceToControlMappingsResponse,
} from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_pb'
import { GridValidRowModel } from '@mui/x-data-grid'
import {
  applyEvidenceFilters,
  applyEvidenceGroupFilters,
  applyEvidenceGroupSort,
  applyEvidenceIndexPageFilters,
  applyEvidenceIndexPageSort,
  getEvidenceLink,
  getSourcesMarkdown,
} from './evidence.helpers'
import {
  EVIDENCE_INDEX_CONFIG,
  EvidenceConfigType,
  EvidenceIndexConfigType,
  EvidenceIndexHeader,
  EvidenceIndexType,
} from './evidence.constants'

export const useInvalidateEvidenceGroups = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await Promise.all([
      mutateFunc(AttachmentPromiseClient.prototype.getLatestEvidenceGroups),
      mutateFunc(AttachmentPromiseClient.prototype.listEvidenceGroups),
      mutateFunc(AttachmentPromiseClient.prototype.getEvidenceGroupById),
    ])
  }, [mutateFunc])
}

export const useLatestEvidenceRequest = (): GetLatestEvidenceRequest => {
  const location = useLocation()

  return useMemo(() => {
    const req = new GetLatestEvidenceRequest()
    const queryParams: ParsedQuery<string> = queryString.parse(
      location.search,
      {
        arrayFormat: 'bracket',
      },
    )
    // Apply all filters for each parameter in URL
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyEvidenceFilters(req, queryParams, filterType),
    )
    return req
  }, [location.search])
}

export const useEvidenceRequest = (): ListEvidenceRequest => {
  const location = useLocation()

  return useMemo(() => {
    const req = new ListEvidenceRequest()
    const queryParams: ParsedQuery<string> = queryString.parse(
      location.search,
      {
        arrayFormat: 'bracket',
      },
    )
    // Apply all filters for each parameter in URL
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyEvidenceFilters(req, queryParams, filterType),
    )
    return req
  }, [location.search])
}

export const useEvidenceAudits = ({
  caption,
  contentId,
  discoveryId,
  relevantDate,
}: {
  caption: string
  contentId: string
  discoveryId: string
  relevantDate: Timestamp
}): GrpcResponse<AuditRecords> => {
  const req = useMemo(() => {
    const evidenceFilter = new EvidenceFilter()
      .setCaption(caption)
      .setContentId(contentId)
      .setDiscoveryId(discoveryId)
      .setRelevantDate(relevantDate)
    const req = new GetAuditsRequest()
    req
      .setEvidenceFilter(evidenceFilter)
      .setIsClosed(new BoolValue().setValue(false))
    return req
  }, [caption, contentId, discoveryId, relevantDate])

  const { response } = useSwrImmutableGrpc(
    AuditPromiseClient.prototype.getAudits,
    req,
    true,
  )
  return NewGrpcResponse(response)
}

export const useEvidenceGroupsRequest = (): ListEvidenceGroupsRequest => {
  const location = useLocation()

  const queryParams: ParsedQuery<string> = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const req = useEvidenceGroupsRequestFromParsedQuery(queryParams)
  return req
}

export const useEvidenceGroupsRequestFromParsedQuery = (
  queryParams: ParsedQuery<string>,
): ListEvidenceGroupsRequest => {
  const newModel = useNewModel(queryParams)
  return useMemo(() => {
    const req = new ListEvidenceGroupsRequest()
    // Default to new model, allow us to swtich to old model if needed for testing
    req.setUseNewModel(newModel)
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyEvidenceGroupFilters(req, queryParams, filterType),
    )
    applyEvidenceGroupSort(req, queryParams)
    return req
  }, [queryParams, newModel])
}

export const useLatestEvidenceGroupsRequestFromParsedQuery = (
  queryParams: ParsedQuery<string>,
): GetLatestEvidenceGroupsRequest => {
  const newModel = useNewModel(queryParams)
  return useMemo(() => {
    const req = new GetLatestEvidenceGroupsRequest()
    // Default to new model, allow us to swtich to old model if needed for testing
    req.setUseNewModel(newModel)
    req.setUseSnappyCode(queryParams.slow == null)
    req.setShowSnappyStats(queryParams.stats != null)
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyEvidenceGroupFilters(req, queryParams, filterType),
    )
    applyEvidenceGroupSort(req, queryParams)
    return req
  }, [queryParams, newModel])
}

export const useLatestEvidenceGroupsRequest =
  (): GetLatestEvidenceGroupsRequest => {
    const location = useLocation()
    const queryParams: ParsedQuery<string> = useMemo(
      () => queryString.parse(location.search, { arrayFormat: 'bracket' }),
      [location.search],
    )
    const req = useLatestEvidenceGroupsRequestFromParsedQuery(queryParams)

    return req
  }

export const useLatestEvidenceGroups = (
  req: GetLatestEvidenceGroupsRequest = new GetLatestEvidenceGroupsRequest(),
  shouldFetch = true,
): GrpcResponse<GetLatestEvidenceGroupsResponse> => {
  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getLatestEvidenceGroups,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useEvidenceGroups = (
  req: ListEvidenceGroupsRequest = new ListEvidenceGroupsRequest(),
  shouldFetch = true,
): GrpcResponse<ListEvidenceGroupsResponse> => {
  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.listEvidenceGroups,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useUpdateCaption = (): ((
  evidenceId: string,
  caption: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(EvidenceServicePromiseClient)
  return useCallback(
    async (evidenceId: string, caption: string) => {
      const request = new UpdateCaptionRequest()
        .setEvidenceId(evidenceId)
        .setCaption(caption)
      await client.updateCaption(request)
    },
    [client],
  )
}

export const useEvidenceSources = (
  contentId: string,
  mime: string,
  isAutomated: boolean,
): (() => Promise<string | null>) => {
  const fetchDocumentBody = useFetchDocumentBodyAllowMultipart(contentId, mime)
  return useCallback(async () => {
    const docBody = await fetchDocumentBody()
    if (docBody) {
      if (isParsedPartArray(docBody)) {
        const sourcesPart = docBody.find(
          (part) =>
            part.headers
              .get('content-disposition')
              ?.includes('receptor_v1.Sources') ||
            (part.headers.get('content-disposition')?.includes('protobuf') &&
              part.headers.get('name')?.includes('receptor_v1.Sources')),
        )
        if (!sourcesPart) return null
        const sourcesBytes = sourcesPart.body as Uint8Array
        const sourcesMarkdown = getSourcesMarkdown(sourcesBytes, true)
        return sourcesMarkdown
      }
      const sources =
        isAutomated && docBody instanceof Uint8Array
          ? getSourcesMarkdown(docBody, false)
          : ''
      return sources
    }
    return null
  }, [isAutomated, fetchDocumentBody])
}

export const useEvidenceConfigs = (
  contentId: string,
  mime: string,
  isAutomated: boolean,
  caption: string,
): (() => Promise<EvidenceConfigType[] | null>) => {
  const fetchDocumentBody = useFetchDocumentBodyAllowMultipart(contentId, mime)
  return useCallback(async () => {
    const docBody = await fetchDocumentBody()
    if (docBody) {
      if (isParsedPartArray(docBody)) {
        const bodyParts = docBody.filter((part) =>
          part.headers.get('content-disposition')?.includes('file'),
        )
        if (!bodyParts.length) return null
        const configs = bodyParts.map((bodyPart) => {
          const body = bodyPart.body
          const mimeType = bodyPart.headers.get('content-type') || ''
          const name =
            bodyPart.headers
              .get('content-disposition')
              ?.split('filename=')[1]
              ?.replace(/['"]/g, '') || ''
          const isMultipartAutomated = mimeType === 'application/protobuf'
          const file = isEvidenceFile(mimeType)
            ? documentBodyAsFile(body, caption, mimeType)
            : null
          return {
            body,
            mimeType,
            isAutomated: isMultipartAutomated,
            file,
            name,
          } as EvidenceConfigType
        })
        return configs
      }
      const file = isEvidenceFile(mime)
        ? documentBodyAsFile(docBody, caption, mime)
        : null
      return [
        {
          body: docBody,
          mimeType: mime,
          isAutomated,
          file,
          name: caption,
        } as EvidenceConfigType,
      ]
    }
    return null
  }, [mime, isAutomated, fetchDocumentBody, caption])
}

export const useEvidenceLinkNoFetch = (
  evidenceInstanceId: string,
  controlId?: string,
  requestId?: string,
): string => {
  const location = useLocation()
  const { pageContext } = useParams()

  const evidenceLink = useMemo(
    () =>
      getEvidenceLink(
        evidenceInstanceId,
        location.search,
        pageContext,
        controlId,
        requestId,
      ),
    [location.search, pageContext, controlId, evidenceInstanceId, requestId],
  )

  return evidenceLink
}

export const useEvidenceIndexHeaders = (
  isReadonly = false,
  hasSharepoint = false,
): EvidenceIndexConfigType => {
  const { auditId } = useInAudit()
  let config = null
  if (isReadonly) {
    config = cloneDeep(EVIDENCE_INDEX_CONFIG[EvidenceIndexType.READONLY])
  } else if (hasSharepoint) {
    config = cloneDeep(EVIDENCE_INDEX_CONFIG[EvidenceIndexType.DMS])
  } else {
    config = cloneDeep(EVIDENCE_INDEX_CONFIG[EvidenceIndexType.LEGACY])
  }
  if (!auditId) {
    config[EvidenceIndexHeader.LINKED_REQUESTS] = false
  }
  return config
}

export const useEvidenceIndexGridTemplateColumns = (
  hasSharepoint: boolean,
): string => {
  const { auditId } = useInAudit()
  if (auditId) {
    return hasSharepoint
      ? 'max-content 1fr  repeat(4, max-content) repeat(2, 12em) max-content repeat(3, min-content)'
      : 'max-content 1fr 10em max-content repeat(2, max-content) repeat(3, min-content)'
  }
  return hasSharepoint
    ? 'max-content 1fr  repeat(3, max-content) repeat(2, 12em) max-content repeat(3, min-content)'
    : 'max-content 1fr 10em max-content repeat(2, max-content) repeat(3, min-content)'
}

export const useEvidenceGroupById = (
  evidenceGroupId: string,
  shouldFetch = true,
): GrpcResponse<GetEvidenceGroupByIdResponse> => {
  const request = new GetEvidenceGroupByIdRequest().setId(evidenceGroupId)

  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getEvidenceGroupById,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useEvidenceGroupIfExists = (
  evidenceGroupId: string,
  shouldFetch = true,
): GrpcResponse<GetEvidenceGroupIfExistsResponse> => {
  const request = new GetEvidenceGroupIfExistsRequest().setId(evidenceGroupId)

  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getEvidenceGroupIfExists,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useGetEvidenceIndexPageFromParsedQuery = (
  queryParams: ParsedQuery<string>,
): GetEvidenceIndexPageRequest => {
  const { auditId } = useInAudit()

  return useMemo(() => {
    const req = new GetEvidenceIndexPageRequest()
      .setAuditId(auditId || '')
      .setShowStats(queryParams.stats != null)
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyEvidenceIndexPageFilters(req, queryParams, filterType),
    )
    applyEvidenceIndexPageSort(req, queryParams)
    return req
  }, [queryParams, auditId])
}

export const useGetEvidenceIndexPageRequest =
  (): GetEvidenceIndexPageRequest => {
    const location = useLocation()
    const queryParams: ParsedQuery<string> = useMemo(
      () => queryString.parse(location.search, { arrayFormat: 'bracket' }),
      [location.search],
    )
    const req = useGetEvidenceIndexPageFromParsedQuery(queryParams)

    return req
  }

export const useEvidenceIndexPage = (
  req: GetEvidenceIndexPageRequest = new GetEvidenceIndexPageRequest(),
  shouldFetch = true,
): GrpcResponse<EvidenceWrapperResponse> => {
  const { auditId } = useInAudit()
  req.setAuditId(auditId || '')

  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getEvidenceIndexPageView,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useControlEvidenceTabRequest = (
  req: GetControlEvidenceTabRequest = new GetControlEvidenceTabRequest(),
  shouldFetch = true,
): GrpcResponse<EvidenceWrapperResponse> => {
  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getControlEvidenceTabView,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useRequestEvidenceTabRequest = (
  req: GetRequestEvidenceTabRequest = new GetRequestEvidenceTabRequest(),
  shouldFetch = true,
): GrpcResponse<EvidenceWrapperResponse> => {
  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getRequestEvidenceTabView,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useEvidenceInstanceList = (
  evidenceId: string,
  controlId?: string,
  shouldFetch = true,
): GrpcResponse<EvidenceWrapperResponse> => {
  const { auditId } = useInAudit()
  const location = useLocation()

  const queryParams: ParsedQuery<string> = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })

  const req = new GetEvidenceInstanceListRequest()
    .setShowStats(queryParams.stats != null)
    .setEvidenceId(evidenceId)
    .setAuditId(auditId || '')
  controlId && req.setControlId(controlId)

  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getEvidenceInstanceList,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useGetRelevantDateFilter = (
  evidenceInstanceId: string,
): GrpcResponse<GetRelevantDateFilterResponse> => {
  const request = new GetRelevantDateFilterRequest().setEvidenceInstanceId(
    evidenceInstanceId,
  )

  const { response } = useSwrImmutableGrpc(
    EvidenceServicePromiseClient.prototype.getRelevantDateFilter,
    request,
    !!evidenceInstanceId,
  )
  return NewGrpcResponse(response)
}

export const useLinkControlsToEvidence = (): ((
  evidenceId: string,
  controlIds: string[],
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(AttachmentPromiseClient)

  return useCallback(
    async (evidenceId: string, controlIds: string[]) => {
      const request = new LinkControlsToEvidenceRequest()
        .setEvidenceId(evidenceId)
        .setControlIdsList(controlIds)
      await client.linkControlsToEvidence(request)
    },
    [client],
  )
}

export const useLinkRequestsToEvidence = (): ((
  evidenceId: string,
  requestIds: string[],
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(AttachmentPromiseClient)

  return useCallback(
    async (evidenceId: string, requestIds: string[]) => {
      const request = new LinkRequestsToEvidenceRequest()
        .setEvidenceId(evidenceId)
        .setRequestIdsList(requestIds)
      await client.linkRequestsToEvidence(request)
    },
    [client],
  )
}

export const useGetEvidenceControls = (
  evidenceId: string,
): GrpcResponse<GetEvidenceControlsResponse> => {
  const { auditId } = useInAudit()
  const request = new GetEvidenceControlsRequest().setEvidenceId(evidenceId)
  auditId && request.setAuditId(auditId)
  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getEvidenceControls,
    request,
    true,
  )
  return NewGrpcResponse(response)
}

export const useDismissEvidenceReadOnlyWarning = (
  evidenceId: string,
  fields: EVIDENCE_READ_ONLY_FIELDS[],
): (() => Promise<void>) => {
  const evidenceClient = useAuthorizedGrpcClient(AttachmentPromiseClient)

  return async () => {
    const request = new UpdateEvidenceReadOnlyFieldsRequest()
      .setEvidenceId(evidenceId)
      .setUpdates(
        new EvidenceReadOnlyFields().setFieldsList([
          ...fields.map((field) =>
            new EvidenceReadOnlyField()
              .setField(field) //
              .setDetails(
                new ReadOnlyFieldDetail().setIsWarningDismissed(
                  new BoolValue().setValue(true),
                ),
              ),
          ),
        ]),
      )
    await evidenceClient.updateEvidenceReadOnlyFields(request)
  }
}

export const useImportedEvidenceModalOnClick = ({
  grcPlatform,
  showModal,
  evidenceId,
  fields,
}: {
  grcPlatform?: GRC_PLATFORM
  showModal?: boolean
  evidenceId: string
  fields: EVIDENCE_READ_ONLY_FIELDS[]
}): ((onClick: () => void) => Promise<void>) => {
  const dismissWarningFunction = useDismissEvidenceReadOnlyWarning(
    evidenceId,
    fields,
  )

  const importedRecordModal = useImportedRecordModal({
    modelType: MODEL_TYPE.EVIDENCE,
    grcPlatform,
    onUpdate: async (onClick: () => void) => {
      await onClick()
    },
    showModal,
    dismissWarningFunction,
  })

  return async (onClick: () => void) => {
    await importedRecordModal(onClick)
  }
}

export const useSearchableEvidence = (
  evidence: EvidenceWrapper[],
): SearchableData<EvidenceWrapper>[] => {
  const searchableEvidence = useMemo(() => {
    return prepareSearchableData(
      evidence,
      (evidence: EvidenceWrapper) => evidence.getId(),
      (evidence: EvidenceWrapper) => {
        const caption = evidence.getCaption()
        const isViaRequest = evidence.getIsViaRequest()
        const isAutomated = evidence.getIsAutomated()
        const isLinkedDocument = evidence.getIsLinkedDocument()
        const actorEmail = evidence.getActorEmail()
        const docImportLink = evidence.getDocImportLink()
        const formattedCaption = formatEvidenceCaption(
          caption,
          isAutomated,
          false,
          isViaRequest,
          false,
        )
        const formattedCaptionText =
          typeof formattedCaption === 'string' ? formattedCaption : ''
        const originalFile = getEvidenceOriginalFile(
          isAutomated,
          isLinkedDocument,
          actorEmail,
          docImportLink,
        )
        const originalFileText =
          typeof originalFile === 'string' ? originalFile : 'View in Source'
        const relevantDate = evidence.getRelevantDate()
        const modifiedDate = evidence.getDocModifiedAt()
        const lastCheckedDate = evidence.getDocScannedAt()
        const relevantDateText = relevantDate
          ? formatTimestamp(relevantDate, DATE_FORMATS.ISO_WITH_TIME)
          : ''
        const modifiedDateText = modifiedDate
          ? formatTimestamp(modifiedDate, DATE_FORMATS.ISO_WITH_TIME)
          : ''
        const lastCheckedDateText = lastCheckedDate
          ? formatTimestamp(lastCheckedDate, DATE_FORMATS.ISO_WITH_TIME)
          : ''
        return [
          formattedCaptionText,
          originalFileText,
          actorEmail,
          relevantDateText,
          modifiedDateText,
          lastCheckedDateText,
        ]
      },
      (evidence: EvidenceWrapper) => {
        return [evidence.getContentId() || '']
      },
    )
  }, [evidence])

  return searchableEvidence
}

export const useSearchableEvidenceInstance = (
  evidenceRows: GridValidRowModel[],
): SearchableData<GridValidRowModel>[] => {
  const searchableEvidence = useMemo(() => {
    return prepareSearchableData(
      evidenceRows,
      (evidenceRow: GridValidRowModel) => Object.values(evidenceRow)[0],
      (evidenceRow: GridValidRowModel) => {
        const rowValues = Object.values(evidenceRow)

        return [...rowValues]
      },
    )
  }, [evidenceRows])

  return searchableEvidence
}

export const useControlToEvidenceMappings = (
  controlIds?: string[],
): GrpcResponse<GetControlToEvidenceMappingsResponse> => {
  const request = new GetControlToEvidenceMappingsRequest()
  if (controlIds && controlIds?.length > 0) {
    request.setControlFilter(new ControlFilter().setControlIdsList(controlIds))
  }
  const { response } = useSwrImmutableGrpc(
    EvidenceManagementPromiseClient.prototype.getControlToEvidenceMappings,
    request,
    true,
  )
  return NewGrpcResponse(response)
}

export const useEvidenceToControlMappings = (
  evidenceIds?: string[],
): GrpcResponse<GetEvidenceToControlMappingsResponse> => {
  const request = new GetEvidenceToControlMappingsRequest()
  if (evidenceIds && evidenceIds?.length > 0) {
    request.setEvidenceFilter(
      new EvidenceManagementEvidenceFilter().setEvidenceIdsList(evidenceIds),
    )
  }
  const { response } = useSwrImmutableGrpc(
    EvidenceManagementPromiseClient.prototype.getEvidenceToControlMappings,
    request,
    true,
  )
  return NewGrpcResponse(response)
}

export const useCreateReceptor = (): ((params: {
  modelId: string
  tenantId: string
  config?: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ReceptorPromiseClient)
  const mutate = useInvalidateReceptorsCache()

  return useCallback(
    async ({ modelId, tenantId, config = '{}' }) => {
      const request = new ReceptorRecord()
        .setModelid(modelId)
        .setTenantid(tenantId)
        .setConfig(config)
        .setIsenabled(false)
        .setIscredvalid(false)

      await client.createReceptor(request)
      await mutate()
    },
    [client, mutate],
  )
}

export function useListDirectoryScanSettings(): GrpcResponse<ListDirectoryScanSettingsResponse> {
  const request = new ListDirectoryScanSettingsRequest()
  const { response } = useSwrImmutableGrpc(
    ReceptorUtilPromiseClient.prototype.listDirectoryScanSettings,
    request,
    true,
  )

  return NewGrpcResponse(response)
}
